import React, { useEffect, useState, useCallback } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router';
import { Col, Row } from 'react-bootstrap';
import PageHeaderContainer from 'containers/PageHeader';
import { FilterSelectionType, GlobalFilterKeyType, GlobalFilterKey } from 'utils/types';
import SalesHighlightsWidget from 'components/SalesHighlightsWidget';
import { periodEndWeekSelector } from 'containers/AccessControl/selectors';
import * as globalSelectors from 'store/global/selectors';
import { ROUTES } from 'utils/routes';
import AccountsOpportunityMatrixWidget from 'components/OpportunityMatrixWidget/AccountsWidget';
import StoresOpportunityMatrixWidget from 'components/OpportunityMatrixWidget/StoresWidget';
import ItemsOpportunityMatrixWidget from 'components/OpportunityMatrixWidget/ItemsWidget';
import Formats from 'utils/formats';
import moment from 'moment';
import _ from 'lodash';
import { FixedHeader } from 'components/OpportunityMatrixTable';
import * as pageHeaderActions from 'containers/PageHeader/actions';
import { globalFilterSelector, detailsFilterSelector } from 'containers/PageHeader/selectors';
import FilterBarContainer from 'containers/PageHeader/FilterBar';
import ReactGA from 'react-ga';
import DistributionChartWidget from 'components/Charts/Widgets/DistributionChartWidget';
import TrendChartWidget from 'components/Charts/Widgets/TrendChartWidget';
import ShareOpportunityChartWidget from 'components/Charts/Widgets/ShareOpportunityChartWidget';
import { PreloadedType } from 'containers/PageHeader/FilterModal';
import usePrevious from 'hooks/use-previous';

interface Props {
  onFilterChange(newFilter: FilterSelectionType, globalFilterKey: GlobalFilterKeyType): void;
  onWeeksChange(weeks: number): void;
  onClearFilter(globalFilterKey: GlobalFilterKeyType): void;
}

const Voids: React.FC<Props> = ({ onFilterChange, onWeeksChange, onClearFilter }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const clientId: string = useSelector(globalSelectors.clientIdSelector);
  const currentBrand: string = useSelector(globalSelectors.currentBrandSelector);
  const globalFilter = useSelector(globalFilterSelector);
  const detailsFilter = useSelector(detailsFilterSelector);
  const periodEndWeek = useSelector(periodEndWeekSelector);
  const isBrandFilterable = useSelector(globalSelectors.isBrandFilterableSelector);

  const [isDetailsPage, setIsDetailsPage] = useState(false);
  const previousLocationSearch: string = usePrevious<string>(location.search);

  let opportunityMatrixWidget;
  let transientGlobalFilter = !isDetailsPage ? { ...globalFilter } : { ...detailsFilter };
  let typesPreloaded: PreloadedType = {
    accounts: false,
    items: false,
    stores: false,
    states: false
  };

  useEffect(() => {
    ReactGA.pageview(ROUTES.VOIDS);
  });

  useEffect(() => {
    if (queryParams.has('account') && !isDetailsPage) {
      setIsDetailsPage(true);
    } else if (!queryParams.has('account') && isDetailsPage) {
      onClearFilter(GlobalFilterKey.DETAILS);
      setIsDetailsPage(false);
    }
  }, [isDetailsPage, onClearFilter, queryParams]);

  useEffect(() => {
    if (location.state) {
      // @ts-ignore
      let { weeks, ...rest } = location.state;
      !isDetailsPage
        ? onFilterChange(rest, GlobalFilterKey.GLOBAL)
        : onFilterChange(rest, GlobalFilterKey.DETAILS);
      onWeeksChange(weeks);
    }
  }, [isDetailsPage, location.state, onFilterChange, onWeeksChange]);

  const updateGlobalFilterFromParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    dispatch(pageHeaderActions.clearGlobalFilter(GlobalFilterKey.DETAILS));
    if (params.has('account')) {
      dispatch(
        pageHeaderActions.updateGlobalFilterArray(
          [decodeURIComponent(params.get('account'))],
          'accounts',
          GlobalFilterKey.DETAILS
        )
      );
    }
    if (params.has('store')) {
      dispatch(
        pageHeaderActions.updateGlobalFilterArray(
          [params.get('store')],
          'stores',
          GlobalFilterKey.DETAILS
        )
      );
    }
  }, [dispatch, location.search]);

  if (queryParams.has('account') && queryParams.has('store')) {
    opportunityMatrixWidget = (
      <ItemsOpportunityMatrixWidget clientId={clientId} globalFilter={transientGlobalFilter} />
    );
    typesPreloaded = {
      accounts: true,
      items: false,
      stores: true,
      states: false
    };
  } else if (queryParams.has('account')) {
    const account = queryParams.get('account');
    opportunityMatrixWidget = (
      <StoresOpportunityMatrixWidget
        clientId={clientId}
        globalFilter={transientGlobalFilter}
        {...(isBrandFilterable && {
          onItemClick: (id, name) =>
            history.push(
              `${ROUTES.VOIDS}?account=${account}&account_name=${encodeURIComponent(
                account
              )}&store=${id}&store_name=${encodeURIComponent(name)}`
            )
        })}
      />
    );
    typesPreloaded = {
      accounts: true,
      items: false,
      stores: false,
      states: false
    };
  } else {
    // All accounts
    opportunityMatrixWidget = (
      <AccountsOpportunityMatrixWidget
        clientId={clientId}
        globalFilter={transientGlobalFilter}
        {...(isBrandFilterable && {
          onItemClick: name =>
            history.push(
              `${ROUTES.VOIDS}?account=${encodeURIComponent(
                name
              )}&account_name=${encodeURIComponent(name)}`
            )
        })}
      />
    );
  }

  useEffect(() => {
    if (previousLocationSearch !== location.search) {
      updateGlobalFilterFromParams();
    }
  }, [
    location.search,
    previousLocationSearch,
    transientGlobalFilter,
    updateGlobalFilterFromParams
  ]);

  return (
    <React.Fragment>
      <Row>
        <PageHeaderContainer
          pagesFilter={transientGlobalFilter}
          globalFilterContext={isDetailsPage ? GlobalFilterKey.DETAILS : GlobalFilterKey.GLOBAL}
          exportPdfAvailable={false}
          template="voidStoresDetailsSeries"
          typesPreloaded={typesPreloaded}
        />
      </Row>
      <FilterBarContainer
        globalFilterContext={isDetailsPage ? GlobalFilterKey.DETAILS : GlobalFilterKey.GLOBAL}
        typesPreloaded={typesPreloaded}
      />
      <Row className="mt-3">
        <Col>
          <SalesHighlightsWidget
            clientId={clientId}
            periodEndWeek={periodEndWeek}
            currentBrand={currentBrand}
            globalFilter={transientGlobalFilter}
            type="voids"
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-3">
          <TrendChartWidget globalFilter={transientGlobalFilter} />
        </Col>
        <Col md={4} className="mt-3">
          <ShareOpportunityChartWidget globalFilter={transientGlobalFilter} />
        </Col>
        <Col md={4} className="mt-3">
          <DistributionChartWidget globalFilter={transientGlobalFilter} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>{opportunityMatrixWidget}</Col>
      </Row>
    </React.Fragment>
  );
};

const mapDispatchToProps = dispatch => ({
  onFilterChange: (newFilter: FilterSelectionType, globalFilterKey: GlobalFilterKeyType) => {
    dispatch(pageHeaderActions.updateGlobalFilterArrays(newFilter, globalFilterKey));
  },
  onWeeksChange: (weeks: number) => {
    dispatch(pageHeaderActions.updateGlobalFilterWeeks(weeks));
  },
  onClearFilter: (globalFilterKey: GlobalFilterKeyType) => {
    dispatch(pageHeaderActions.clearGlobalFilter(globalFilterKey));
  }
});

export default connect(null, mapDispatchToProps)(Voids);

export const getColor = state => {
  switch (state) {
    case 'kept':
      return 'green';
    case 'lost':
      return 'yellow';
    case 'neverSeen':
      return 'red';
    case 'filled':
      return 'purple';
    default:
      return null;
  }
};

export type FixedRowAttribute = {
  name: string;
  format?(value: any): string;
};

export const createTableProps = (
  data,
  itemHeaders: Array<any>,
  fixedHeaders: Array<FixedHeader>,
  fixedRowAttributes: Array<FixedRowAttribute>,
  onRowClick?: (row: any) => void
) => {
  const itemHeadersSortedBySales = _.orderBy(itemHeaders, ['sales'], ['desc']);
  return {
    headers: {
      fixed: fixedHeaders,
      variable: itemHeadersSortedBySales.map(header => {
        return {
          value: header.voids,
          tooltip: (
            <React.Fragment>
              <div>Last scanned</div>
              <div>{Formats.date(header.recentSaleDate, 'MM/DD/YY')}</div>
              <div>{`Dollars: ${Formats.currency(header.sales)}`}</div>
            </React.Fragment>
          )
        };
      })
    },
    rows: data.map(row => {
      const maxRecentSaleDate = moment.max(
        row.items
          .filter(item => item.recentSaleDate !== null)
          .map(item => moment.parseZone(item.recentSaleDate))
      );
      const itemsSortedByHeaderSales = [];
      itemHeadersSortedBySales.forEach(itemHeader => {
        // For each header, which is sorted by sales
        const found = row.items.find(row => row.upc === itemHeader.upc);
        if (found) {
          itemsSortedByHeaderSales.push(found);
        } else {
          console.log('Item header without related item');
          // Item found on header but not on row, add it with empty values
          itemsSortedByHeaderSales.push({
            distributionState: 'neverSeen',
            recentSaleDate: null,
            sales: 0,
            upc: itemHeader.upc
          });
        }
      });
      return {
        id: row.name,
        onRowClick: onRowClick ? () => onRowClick(row) : undefined,
        fixed: fixedRowAttributes
          .map(attribute => ({
            value: attribute.format ? attribute.format(row[attribute.name]) : row[attribute.name],
            tooltip: undefined
          }))
          .concat([
            {
              value: row.voids,
              tooltip: (
                <React.Fragment>
                  <div>Last scanned</div>
                  <div>{maxRecentSaleDate.format('MM/DD/YY')}</div>
                  <div>{`Dollars: ${Formats.currency(row.sales)}`}</div>
                </React.Fragment>
              )
            }
          ]),
        variable: itemsSortedByHeaderSales.map(rowItem => {
          return {
            value:
              rowItem.recentSaleDate !== null
                ? moment.parseZone(rowItem.recentSaleDate).format('MM/DD/YY')
                : '-',
            color: getColor(rowItem.distributionState),
            tooltip: <span>{`Dollars: ${Formats.currency(rowItem.sales)}`}</span>
          };
        })
      };
    }),
    topHeaders: itemHeadersSortedBySales.map(header => {
      return {
        headerLines: [header.description, `(${header.upc})`],
        tooltip: <span>{`Dollars: ${Formats.currency(header.sales)} | ${header.upc}`}</span>
      };
    })
  };
};
